import { getEtablissementLabel } from '@/components/Etablissement/etablissement.utils';
import {
  useGetEtablissementQuery,
  useGetEtablissementsQuery,
} from '@/features/api/endpoints/etablissementEndpoints';
import { Loader, Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useState } from 'react';

type Props = {
  value: string | null;
  onChange: (value: string | null) => void;
  label: string;
  placeholder: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  limit?: number;
  minSearchLength?: number;
};

const EtablissementAutocomplete = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  disabled,
  required,
  limit = 20,
  minSearchLength = 2,
}: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 300);

  const trimmedSearchValue = debouncedSearchValue.trim();
  const shouldSearch = trimmedSearchValue.length >= minSearchLength;

  const { data: searchResults = [], isFetching: isFetchingSearchResults } =
    useGetEtablissementsQuery(
      shouldSearch
        ? {
            sort: ['name'],
            limit,
            filter: {
              _or: [
                {
                  name: {
                    _icontains: trimmedSearchValue,
                  },
                },
                {
                  finess: {
                    _icontains: trimmedSearchValue,
                  },
                },
              ],
            },
          }
        : skipToken,
    );

  const { data: selectedEtablissement, isFetching: isFetchingSelectedEtablissement } =
    useGetEtablissementQuery(value ?? skipToken);

  const options = useMemo(() => {
    const initialOptions = searchResults.map(etablissement => ({
      value: String(etablissement.finess),
      label: getEtablissementLabel(etablissement),
    }));

    if (!selectedEtablissement) {
      return initialOptions;
    }

    const selectedOptionValue = String(selectedEtablissement.finess);
    const hasSelectedOption = initialOptions.some(option => option.value === selectedOptionValue);

    if (hasSelectedOption) {
      return initialOptions;
    }

    return [
      {
        value: selectedOptionValue,
        label: getEtablissementLabel(selectedEtablissement),
      },
      ...initialOptions,
    ];
  }, [searchResults, selectedEtablissement]);

  return (
    <Select
      searchable
      clearable
      value={value !== null ? String(value) : null}
      onChange={nextValue => onChange(nextValue !== null ? nextValue : null)}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      data={options}
      label={label}
      placeholder={placeholder}
      error={error}
      disabled={disabled}
      required={required}
      rightSection={
        isFetchingSearchResults || isFetchingSelectedEtablissement ? <Loader size="xs" /> : null
      }
    />
  );
};

export default EtablissementAutocomplete;
